import React, {useCallback, useState, useEffect} from 'react';
import {
    Page, Card, Button, Thumbnail, Stack, Heading, Checkbox
} from '@shopify/polaris';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import {productService} from '../../services/product';
import Product from '../../types/product';
import ProductSelectModal from './ProductSelectModal';

import './common.css';


function Common(props: any){
    const {
        selectedParentProduct,
        selectedChildrenProducts,
        handleParentProductChange,
        handleChildrenProductsChange,
        ignoreParent,
        onSave
    } = props;

    const [active, setActive] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isChildrenProductsSelectionChanged, setIsChildrenProductsSelectionChanged] = useState(false);
    const [isOrderChanged, setIsOrderChanged] = useState(false);
    const [activeMultiSelectModal, setActiveMultiSelectModal] = useState(false);
    const [ignoreParentVariantsChecked, setIgnoreParentVariantsChecked] = useState(ignoreParent || false);
    const handleParentVariantsCheckBox = useCallback((newChecked) => setIgnoreParentVariantsChecked(newChecked), []);
    const [allProducts, setAllProducts] = useState<Product[]>([]);

    const grid = 8;

    // a little function to help us with reordering the result
    const reorder = (list: Product[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onDragEnd = (result: any) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const items = reorder(
            selectedChildrenProducts,
            result.source.index,
            result.destination.index
        );
        handleChildrenProductsChange(items);
        setIsOrderChanged(true);
    }

    const getListStyle = (isDraggingOver: any) => ({
        background: isDraggingOver ? "lightblue" : "transparent",
        //padding: grid,
        //width: 250
    });
    const getItemStyle = (isDragging: any, draggableStyle: any) => ({
        // some basic styles to make the items look a bit nicer
        userSelect: "none",
        padding: grid * 2,
        margin: `0 0 ${grid}px 0`,
        
        // change background colour if dragging
        background: isDragging ? "lightgreen" : "transparent",
        border: "1px solid #f8f8f8",
        
        // styles we need to apply on draggables
        ...draggableStyle
    });

    const handleModalToggle = useCallback(() => {
        setActive(!active);
    }, [active]);

    const handleMultiSelectModalToggle = useCallback(() => {
        setActiveMultiSelectModal(!activeMultiSelectModal);
    }, [activeMultiSelectModal]);

    const handleOnSave = () => {
        //console.log('HAH', isOrderChanged, isChildrenProductsSelectionChanged)
        if( isOrderChanged || isChildrenProductsSelectionChanged || ignoreParentVariantsChecked )
            onSave(isOrderChanged, isChildrenProductsSelectionChanged, ignoreParentVariantsChecked);
    }

    useEffect(() => {
        productService.all().then(prods => {
            setAllProducts(prods);
            setIsLoading(false);
        });
    }, []);

    return (<Page>
        <Card title="New Product Group" sectioned>
            { selectedParentProduct ? (
                <Stack alignment="center">
                    <Thumbnail source={selectedParentProduct.image ? selectedParentProduct.image.src : window.location.origin +  '/placeholder.png'} alt={selectedParentProduct.title} />
                    <Heading>
                        {selectedParentProduct.title}
                    </Heading>
                </Stack>
            ) : (
                <div>
                    <p>Here goes the content</p><br/>
                    <Button onClick={handleModalToggle}>Select parent product</Button>
                </div>
            ) }
        </Card>
        { selectedParentProduct ? (
            <div className="productGroupChildren">
                <Card sectioned>
                    <p>Select other products in the group <Checkbox label="Ignore Parent Variants" checked={ignoreParentVariantsChecked} onChange={handleParentVariantsCheckBox} /></p><br/>
                    {selectedChildrenProducts && selectedChildrenProducts.length && (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided, snapshot) => (
                                    <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    style={getListStyle(snapshot.isDraggingOver)}
                                    >
                                        {selectedChildrenProducts.map((item: Product, index: number) => {
                                            return (
                                                <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style
                                                        )}
                                                        >
                                                            <Stack alignment="center" key={item.id}>
                                                                <Stack.Item>
                                                                    <Thumbnail source={item.image ? item.image.src : '/placeholder.png'} alt={item.title} />
                                                                </Stack.Item>
                                                                <Stack.Item fill>
                                                                    <Heading>{item.title}</Heading>
                                                                </Stack.Item>
                                                                {/*<Stack.Item>
                                                                    <Button destructive size="slim" onClick={() => {
                                                                        console.log('clicked!');
                                                                        return false;
                                                                    }}>remove</Button>
                                                                </Stack.Item>*/}
                                                            </Stack>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                    <Button onClick={handleMultiSelectModalToggle}>Select product group</Button>
                </Card>
            </div>
        ) : null }

        {selectedParentProduct && selectedChildrenProducts && (<Card sectioned>
            <Button primary onClick={handleOnSave}>Save</Button>
        </Card>) }

        <ProductSelectModal
            isLoading={isLoading}
            active={active}
            title="Select Parent Product"
            items={allProducts}
            selectedItem={selectedParentProduct}
            onPrimaryButtonClick={handleModalToggle}
            OnClose={handleModalToggle}
            onItemSelection={handleParentProductChange}
        />

        {selectedParentProduct && <ProductSelectModal
                isLoading={isLoading}
                active={activeMultiSelectModal}
                title="Select Children Products"
                items={allProducts.filter(prod => prod.id !== selectedParentProduct.id)}
                onPrimaryButtonClick={(selectedItems: Product[]) =>{
                    handleChildrenProductsChange(selectedItems);
                    handleMultiSelectModalToggle();
                }}
                OnClose={handleMultiSelectModalToggle}
                multiSelection={true}
                defaultSelectedItems={selectedChildrenProducts}
                onSelectionChange={() => {
                    setIsChildrenProductsSelectionChanged(true);
                }}
        /> }
    </Page>);
}

export default Common;